import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../../../../components/Loading/Loading";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useGetUserExtrabonus from "../../../../hooks/useGetUserExtrabonus";
import useGetUserRules from "../../../../hooks/useGetUserRules";
import { useToggle } from "../../../../hooks/useToggle";
import localeText from "../../../../locale";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import { formatDate } from "../../../../utilities/formatDate";
import { formatPrice } from "../../../../utilities/formatPrice";
import "./UserDashboardHome.css";
import ReferralExplanationModal from "./components/referralExplanationModal";
import ReferralLink from "./components/referralLink";
import PagamentoSeguro from "../../../images/pagamentoSeguro.png";
import paypalImg from "../../../../pages/images/paypal.png";
import masterCardImg from "../../../../pages/images/masterCard.png";
import americanExpress from "../../../../pages/images/expressoAmericano.png";
import visa from "../../../../pages/images/visa.png";
import { formatFloatToInteger } from "../../../../utilities/formatFloatToInteger";
import UserInitialsAvatar from "../../../../components/UserInitialsAvatar/UserInitialsAvatar";

const redColor = "#eb4034";
const greenColor = "#46a748";

const UserDashboardHome = (props) => {
  const [chartData, setChartData] = useState(null);
  const [intervalGraph, setIntervalGraph] = useState(30);
  const toast = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [freeRenewalsRemaining, setFreeRenewalsRemaining] = useState(0);

  const checkFreeRenewalsRemaining = async () => {
    try {
      const response = await getUserInfoService.getSubscriptionRenewals(
        localStorage.getItem("__extraconomy_username"),
        localStorage.getItem("__extraconomy_token")
      );

      const data = await response.json();

      if (data === null) {
        setFreeRenewalsRemaining(0);
        return 0;
      } else {
        setFreeRenewalsRemaining(data.remainingRenewals);
        return data.remainingRenewals;
      }
    } catch (error) {
      setFreeRenewalsRemaining(0);
      return 0;
    }
  };

  const [activationFormattedData, setActivationFormattedData] = useState("");
  const [activationDaysLeft, setActivationDaysLeft] = useState(0);

  const subscriptionFormattedData = props?.subscriptionInfo?.subscriptionFormattedData;
  const subscriptionDaysLeft = props?.subscriptionInfo?.subscriptionDaysLeft;

  const [isSpecialCustomer, setIsSpecialCustomer] = useState(false);
  const [isStore, setIsStore] = useState(false);
  const [isPromoter, setIsPromoter] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const {
    value: modalLinkRefereeExplanation,
    setAsFalse: setNotVisibleModalLinkRefereeExplanation,
    setAsTrue: setVisibleModalLinkRefereeExplanation,
  } = useToggle();

  const { showErrorToast: newShowErrorToast } = useToastContext();
  const { isLoading: isLoadingUserRules, userRules } = useGetUserRules({
    onError: ({ code }) => newShowErrorToast({ code }),
  });

  const { isLoading: isLoadinguseUserExtrabonus, userExtrabonus } = useGetUserExtrabonus({
    onError: ({ code }) => newShowErrorToast({ code }),
    typeUser: props.userTypeInfo,
  });

  useEffect(() => {
    if (props.activationInfo) {
      setActivationFormattedData(`${formatDate(
        new Date(props?.activationInfo?.activation?.dateBegin)
      )} -
        ${formatDate(new Date(props?.activationInfo?.activation?.dateEnd))}`);

      setActivationDaysLeft(
        ((new Date(props?.activationInfo?.activation?.dateEnd) - Date.now()) / 86400000) | 0
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalGraph]);

  useEffect(() => {
    checkFreeRenewalsRemaining();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartOptions = {
    responsive: true,
    hover: {
      mode: "index",
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: localeText("date"),
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: localeText("quantity"),
        },
      },
    },
  };

  const getUserInfoService = new GetUserInfoService();

  useEffect(() => {
    let cancel = false;

    getUserInfoService
      .getUserPlan(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username")
      )
      .then((plan) => {
        if (cancel) return;

        if (plan === "Customer") setIsSpecialCustomer(true);
        if (plan === "Promoter") setIsPromoter(true);
        if (plan === "Store") setIsStore(true);
        else {
          if (plan === "Manager" || plan === "Partner" || plan === "Seller") setShowAlert(true);
        }
      });

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancel = false;

    getUserInfoService
      .getStatisticsGraph(
        localStorage.getItem("__extraconomy_token"),
        intervalGraph,
        localStorage.getItem("__extraconomy_username")
      )
      .then((res) => {
        if (cancel) return;

        const labelsArr = [];
        const dataArr = [];

        if (intervalGraph === 30) {
          for (let i = 0; i <= intervalGraph; i++) {
            const dateNow = new Date(new Date().setUTCDate(new Date().getUTCDate() - i));
            labelsArr.unshift(formatDate(dateNow));

            let foundMatch = false;

            res.result.forEach((date) => {
              const dateNowStats = new Date(date[1]);
              if (formatDate(dateNowStats) === formatDate(dateNow) && !foundMatch) {
                dataArr.unshift(date[0]);
                foundMatch = true;
              }
            });

            if (!foundMatch) dataArr.unshift(0);
          }

          setChartData({
            labels: labelsArr,
            datasets: [
              {
                label: localeText("numberOfUsersInNetwork"),
                data: dataArr,
                backgroundColor: ["#46a748"],
                fill: false,
                pointRadius: 3,
                tension: 0.4,
              },
            ],
          });
        } else {
          for (
            let i = new Date().getUTCMonth();
            i >= new Date().getUTCMonth() - (intervalGraph / 30 - 1);
            i--
          ) {
            let month = i;

            let isLastYear = false;
            if (month < 0) {
              month = month + 12;
              isLastYear = true;
            }

            let usersInMonth = 0;

            // X-axis labels (month names)
            const monthNamesArr = localeText("monthNames");
            labelsArr.unshift(
              `${monthNamesArr[month]} ${
                isLastYear ? new Date().getFullYear() - 1 : new Date().getFullYear()
              }`
            );

            res.result.forEach((date) => {
              // Gets the date from the date object (0 is quantity of users added to network, 1 is the date itself)
              const dateNowStats = new Date(date[1]);

              if (dateNowStats.getUTCMonth() === month) {
                // Increments the number of users in month by the value in index 0 (described by the comment above)
                usersInMonth = usersInMonth + date[0];
              }
            });

            dataArr.unshift(usersInMonth);
          }

          setChartData({
            labels: labelsArr,
            datasets: [
              {
                label: localeText("numberOfUsersInNetwork"),
                data: dataArr,
                backgroundColor: ["#46a748"],
                fill: true,
                pointRadius: 3,
                tension: 0.4,
              },
            ],
          });
        }
      });

    return () => (cancel = true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalGraph]);

  const dateNow = Date.now();
  const dateBegin = new Date(props?.activationInfo?.activation?.dateBegin);
  const dateEnd = new Date(props?.activationInfo?.activation?.dateEnd);

  const lengthCalculation = 100 - ((dateNow - dateBegin) * 100) / (dateEnd - dateBegin);

  const finalLength = lengthCalculation < 0 ? 0 : lengthCalculation;

  const hasAddress =
    !!props?.userInfo?.city &&
    !!props?.userInfo?.state &&
    !!props?.userInfo?.country &&
    !!props?.userInfo?.postalCode &&
    !!props?.userInfo?.address;

  return (
    <>
      {isLoadingUserRules || isLoadinguseUserExtrabonus ? (
        <Loading classNameIcon={"text-4xl"} />
      ) : (
        <>
          <ReferralExplanationModal
            isVisible={modalLinkRefereeExplanation}
            setNotVisible={setNotVisibleModalLinkRefereeExplanation}
          />

          <div className="layout-dashboard">
            <Toast ref={toast} />
            {showAlert && (
              <div
                className="border-2 card border-round"
                style={{ borderColor: hasAddress ? greenColor : redColor }}
              >
                {!hasAddress ? (
                  <>
                    <p style={{ color: redColor }}>{localeText("notEnabled")}</p>
                    <Link to="/dashboard-editprofile">
                      <Button label={localeText("editProfile")} />
                    </Link>
                  </>
                ) : (
                  <p style={{ color: greenColor }}>{localeText("enabled")}</p>
                )}
              </div>
            )}
            <div className="flex align-items-center justify-content-between">
              {!JSON.parse(localStorage.getItem("__extraconomy_ismobile__")) && (
                <Link to="/shop">
                  <Button
                    className="mt-2 mb-2 ml-2"
                    icon="pi pi-shopping-cart"
                    label={localeText("shop")}
                  />
                </Link>
              )}
              <div className="flex justify-content-center align-items-center gap-2">
                <div className="flex justify-content-center align-items-center gap-2">
                  <p className="m-0 text-xs sm:block hidden">{localeText("safePayment")}</p>
                  <img
                    src={PagamentoSeguro}
                    alt=""
                    style={{ width: "20px" }}
                    title={localeText("safePayment")}
                  />
                </div>
                <img
                  src={paypalImg}
                  alt="paypal"
                  title="paypal"
                  style={{ width: "20px", height: "20px" }}
                />
                <img
                  src={americanExpress}
                  alt="americanExpress"
                  title="americanExpress"
                  style={{ width: "20px", height: "20px" }}
                />
                <img
                  src={masterCardImg}
                  alt="mastercard"
                  title="mastercard"
                  style={{ width: "30px", height: "20px" }}
                />
                <img src={visa} alt="visa" style={{ width: "35px", height: "10px" }} title="visa" />
              </div>
            </div>
            <div className="flex flex-wrap">
              {(props.userTypeInfo === "Manager" ||
                props.userTypeInfo === "Partner" ||
                props.userTypeInfo === "Seller" ||
                props.userTypeInfo === "Store") &&
                props.lifetimeBonusInfo && (
                  <div className="w-3 col sm:w-full" style={{ minWidth: "15em" }}>
                    <div className="overview-box views h-full" style={{ borderRadius: "1.5rem" }}>
                      <i className="overview-icon pi pi-star"></i>
                      <span className="overview-title">
                        <b>{localeText("bonus").toUpperCase()}</b>
                      </span>
                      <div className="overview-numbers">
                        <ul className="list-none p-0">
                          <li>
                            {localeText("balance")}: {props.currency}{" "}
                            {formatPrice(props.lifetimeBonusInfo.balance)}
                          </li>
                          <li className="py-2">
                            {localeText("inbound")}: {props.currency}{" "}
                            {formatPrice(props.lifetimeBonusInfo.income)}
                          </li>
                          <li>
                            {localeText("outbound")}: {props.currency}{" "}
                            {formatPrice(props.lifetimeBonusInfo.outcome)}
                          </li>
                          <div className="overview-subinfo mt-4 mb-0 text-0">
                            {localeText("every")}{" "}
                            <b>
                              {props.currency} {formatPrice(10)}
                            </b>
                            {localeText("AmountIsAutomaticallyTransferredToWallet")}
                          </div>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

              {props.walletInfo && (
                <div className="w-3 col sm:w-full" style={{ minWidth: "15em" }}>
                  <div className="overview-box checkin h-full" style={{ borderRadius: "1.5rem" }}>
                    <i className="overview-icon pi pi-wallet"></i>
                    <span className="overview-title">
                      <b>{localeText("wallet").toUpperCase()}</b>
                    </span>
                    <div className="overview-numbers">
                      <ul className="list-none p-0">
                        <li>
                          <div className="flex">
                            {localeText("balance")}: {props.currency}{" "}
                            {formatPrice(props.walletInfo.balance)}
                            <Tooltip target=".extracoin-tooltip" />
                            <i
                              className="pi pi-info-circle ml-1 extracoin-tooltip cursor-pointer text-200"
                              data-pr-tooltip={`${props.currency}${formatPrice(
                                props.walletInfo.balancecomission
                              )} (${localeText("balanceCommission")}) + ${
                                props.currency
                              }${formatPrice(props.walletInfo.balancesale)} (${localeText(
                                "balanceSale"
                              )})`}
                              data-pr-position="right"
                              data-pr-at="right+5 top"
                              data-pr-my="left center-2"
                              tooltipoptions={{ event: "focus" }}
                            ></i>
                          </div>
                        </li>
                        <li className="py-2">
                          {localeText("inbound")}: {props.currency}{" "}
                          {formatPrice(props.walletInfo.income)}
                        </li>
                        <li>
                          {localeText("outbound")}: {props.currency}{" "}
                          {formatPrice(props.walletInfo.outcome)}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {props.extracoinInfo && (
                <div className="w-3 col sm:w-full" style={{ minWidth: "15em" }}>
                  <div className="overview-box users h-full" style={{ borderRadius: "1.5rem" }}>
                    <i className="overview-icon pi pi-dollar"></i>
                    <span className="overview-title">
                      <b>EXTRACREDIT</b>
                    </span>
                    <div className="overview-numbers">
                      <ul className="list-none p-0 mb-0">
                        <li>
                          {localeText("balance")}: {formatPrice(props.extracoinInfo.balance)}
                        </li>
                        <li className="py-2">
                          {localeText("inbound")}: {formatPrice(props.extracoinInfo.income)}
                        </li>
                        <li>
                          {localeText("outbound")}: {formatPrice(props.extracoinInfo.outcome)}
                        </li>
                      </ul>
                    </div>
                    <div className="overview-subinfo mt-4 mb-0 text-0">
                      <b>1 EXTRACREDIT = {props.currency} 1</b>
                      <br />
                      {props.extracoinPercentage && (
                        <>
                          {localeText("currentlyYouCan")}{" "}
                          <b>{props.extracoinPercentage.percent}%</b>
                          {localeText("ofYourExtracoin")}
                          <br />
                          {localeText("extracoinOnlyBetweenUsers")}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {userExtrabonus && (
                <div className="w-3 col sm:w-full" style={{ minWidth: "15em" }}>
                  <div className="overview-box sales h-full" style={{ borderRadius: "1.5rem" }}>
                    <i className="overview-icon pi pi-money-bill"></i>
                    <span className="overview-title">
                      <b>BONUS ADS</b>
                    </span>
                    <div className="overview-numbers">
                      <ul className="list-none p-0 mb-0">
                        <li>
                          {localeText("balance")}: {formatFloatToInteger(userExtrabonus.balance)}
                        </li>
                        <li className="py-2">
                          {localeText("balanceForExternalAds")}:{" "}
                          {formatFloatToInteger(userExtrabonus.balanceElegibleToExternalAd)}
                          <Tooltip target=".extracoin-tooltip" />
                          <i
                            className="pi pi-info-circle ml-1 extracoin-tooltip cursor-pointer text-200"
                            data-pr-tooltip={`${localeText(
                              "youCanUseThisAmountToMakeExternalAds"
                            )}`}
                            data-pr-position="left"
                            data-pr-at="left center"
                            tooltipoptions={{ event: "focus" }}
                          ></i>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {props.userInfo && props.userTypeInfo && props.directsInfo && (
              <div className="grid mt-3 align-items-start">
                <div className="col-12 lg:col-8">
                  <div className="card card-w-title statistics">
                    <h5>{localeText("stats")}</h5>
                    <Button
                      className="mr-2 mb-2"
                      label={`30 ${localeText("days")}`}
                      onClick={() => setIntervalGraph(30)}
                    />
                    <Button
                      className="mr-2 mb-2"
                      label={`3 ${localeText("months")}`}
                      onClick={() => setIntervalGraph(90)}
                    />
                    <Button
                      className="mr-2 mb-2"
                      label={`6 ${localeText("months")}`}
                      onClick={() => setIntervalGraph(180)}
                    />
                    <Button
                      className="mb-2"
                      label={`1 ${localeText("year")}`}
                      onClick={() => setIntervalGraph(360)}
                    />
                    <Chart type="bar" data={chartData} options={chartOptions} />
                  </div>
                </div>

                <div className="col-12 lg:col-4">
                  <div className="user-card card">
                    <div className="user-card-content">
                      <div className="flex align-items-center ml-4 mt-4 mb-2">
                        <UserInitialsAvatar
                          avatarUrl={props?.userInfo?.urlAvatar}
                          firstName={props?.userInfo?.firstname}
                          lastName={props?.userInfo?.surname}
                          className="m-0"
                          style={{ width: "50px", height: "50px" }}
                          alt="babylon-layout"
                        />
                        <div className="user-card-name text-900 top-0 ml-4">
                          <span>
                            {props.userInfo.firstname} {props.userInfo.surname}
                          </span>
                        </div>
                      </div>

                      <div className="user-detail">
                        <ul>
                          <li className="clearfix grid align-items-center">
                            <div className="col-2">
                              <i className="pi pi-user"></i>
                            </div>
                            <div className="col-10 flex flex-column">
                              <div>
                                <span className="project-title">{localeText("profile")}:</span>
                                <span className="project-detail capitalize">
                                  {userRules?.profile?.toLowerCase()}
                                </span>
                              </div>
                              <div>
                                <span className="project-title">{localeText("plan")}:</span>
                                <span className="project-detail">{userRules?.plan}</span>
                              </div>
                            </div>
                          </li>

                          {!isSpecialCustomer && props.subscriptionInfo && (
                            <li className="clearfix grid align-items-center">
                              <div className="col-2">
                                <i className="pi pi-dollar"></i>
                              </div>

                              <div className="col-6">
                                <p className="project-title m-0">{localeText("subscription")}:</p>
                                <p className="project-detail text-sm text-600">
                                  {subscriptionFormattedData}
                                </p>
                              </div>
                              <div className="col-4">
                                <p className="text-sm m-0 text-center text-600">
                                  {subscriptionDaysLeft < 0 ? (
                                    <>{localeText("notActive")}</>
                                  ) : (
                                    <>
                                      {subscriptionDaysLeft}
                                      {localeText("daysLeft")}
                                    </>
                                  )}
                                </p>
                                <div
                                  className="project-progressbar w-full mt-1"
                                  style={{ height: "5px" }}
                                >
                                  {props.subscriptionInfo && (
                                    <div
                                      className="project-progressbar-value"
                                      style={{
                                        width: `${
                                          100 -
                                          ((Date.now() -
                                            new Date(props.subscriptionInfo.dateBegin)) *
                                            100) /
                                            (new Date(props.subscriptionInfo.dateEnd) -
                                              new Date(props.subscriptionInfo.dateBegin))
                                        }%`,
                                      }}
                                    ></div>
                                  )}
                                </div>
                              </div>
                            </li>
                          )}
                          {isPromoter &&
                            props.activationInfo &&
                            props.activationInfo.activation && (
                              <li
                                className="clearfix grid align-items-center"
                                style={{ position: "relative" }}
                              >
                                <div style={{ position: "absolute", right: 10, top: 20 }}>
                                  <Button
                                    icon="pi pi-info"
                                    rounded
                                    className="p-button-info ml-2 text-primary"
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      border: "1px solid black",
                                      width: "25px",
                                      height: "25px",
                                      right: "-20px",
                                      top: "10px",
                                    }}
                                    onClick={() => setShowInfo((oldState) => !oldState)}
                                  />
                                  {showInfo && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        zIndex: 2,
                                        width: "300px",
                                        right: "10px",
                                        top: "20px",
                                        background: "white",
                                        padding: "5px",
                                      }}
                                    >
                                      <p>{localeText("activationRequirements")}</p>
                                    </div>
                                  )}
                                </div>
                                <div className="col-2">
                                  <i className="pi pi-money-bill"></i>
                                </div>

                                <div className="col-6">
                                  <p className="project-title m-0">{localeText("activation")}:</p>
                                  <p className="project-detail text-sm text-600">
                                    {activationFormattedData}
                                  </p>
                                </div>
                                <div className="col-4">
                                  <p className="text-sm m-0 text-center text-600">
                                    {activationDaysLeft < 0 ? (
                                      <>{localeText("notActive")}</>
                                    ) : (
                                      <>
                                        {activationDaysLeft}
                                        {localeText("daysLeft")}
                                      </>
                                    )}
                                  </p>
                                  <div
                                    className="project-progressbar w-full mt-1"
                                    style={{ height: "5px" }}
                                  >
                                    <div
                                      className="project-progressbar-value"
                                      style={{
                                        width: `${finalLength}%`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </li>
                            )}
                          <li>
                            <p style={{ fontWeight: "bold" }}>{`${localeText(
                              "freeRenewalsRemaining"
                            )}: ${freeRenewalsRemaining}`}</p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {(props.userTypeInfo === "Manager" ||
                    props.userTypeInfo === "Partner" ||
                    props.userTypeInfo === "Store") &&
                    props.directsInfo &&
                    props.allNodesInfo && (
                      <div className="w-full card">
                        <div className="card-w-title w-full flex flex-row justify-content-between align-items-center">
                          <h5 className="mb-0">{localeText("network").toUpperCase()}</h5>
                          <i className="overview-icon pi pi-sitemap"></i>
                        </div>
                        <div className="overview-numbers">
                          <ul className="list-none p-0">
                            <li>
                              {localeText("directs")}: {props.directsInfo.length}
                            </li>
                            <li className="py-2">
                              {localeText("indirects")}:{" "}
                              {props.allNodesInfo.length - props.directsInfo.length}
                            </li>
                            <li>
                              {localeText("all")}: {props.allNodesInfo.length}
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}

                  <div>
                    <div className="card card-w-title team">
                      <h5>{localeText("directs")}</h5>
                      {props.userTypeInfo !== "Customer" ? (
                        <ul>
                          {props.directsInfo.slice(0, 4).map((item) => (
                            <li key={item.nodeusername}>
                              <UserInitialsAvatar
                                avatarUrl={item.urlavatar}
                                fullName={item.node}
                                className="layout-dashboard-urlavatar"
                                alt="user-avatar"
                              />
                              <div className="team-box">
                                <span className="team-member">{item.node}</span>
                                {item.plan === "Customer" && (
                                  <span
                                    className="team-member-role"
                                    style={{ backgroundColor: "#2196F3" }}
                                  >
                                    {item.plan}
                                  </span>
                                )}
                                {item.plan === "Partner" && (
                                  <span
                                    className="team-member-role"
                                    style={{ backgroundColor: "#4CAF50" }}
                                  >
                                    {item.plan}
                                  </span>
                                )}
                                {item.plan === "Manager" && (
                                  <span
                                    className="team-member-role"
                                    style={{ backgroundColor: "#F7B005" }}
                                  >
                                    {item.plan}
                                  </span>
                                )}
                                {item.plan === "Seller" && (
                                  <span
                                    className="team-member-role"
                                    style={{ backgroundColor: "#272360" }}
                                  >
                                    {item.plan}
                                  </span>
                                )}
                                {/promoter/i.test(item.plan) && (
                                  <span
                                    className="team-member-role"
                                    style={{ backgroundColor: "#9C27B0" }}
                                  >
                                    {item.plan}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                          <div className="mt-2">
                            {props.directsInfo.length > 0 && (
                              <Link to="/dashboard-networkdirects">
                                {localeText("seeAllDirects")}
                              </Link>
                            )}

                            {props.directsInfo.length === 0 && (
                              <div style={{ display: "table", height: "15em", overflow: "hidden" }}>
                                <div style={{ display: "table-cell", verticalAlign: "middle" }}>
                                  <p className="text-lg">{localeText("youDontHaveAny")}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <li className="mt-4 p-0">
                            <h5>
                              {localeText("referralLinks")}{" "}
                              <i
                                onClick={setVisibleModalLinkRefereeExplanation}
                                className="pi pi-info-circle text-gray-400 extracoin-tooltip cursor-pointer text-200"
                                data-pr-position="right"
                                data-pr-at="right+5 top"
                                data-pr-my="left center-2"
                                tooltipoptions={{ event: "focus" }}
                              />
                            </h5>
                            <ul className="p-0 m-0">
                              <ReferralLink
                                text="Customer"
                                backgroundColor="#2196F3"
                                referralUrl={`https://extraconomy.com/specialcustomer?link=${props.userInfo.username}`}
                              />
                              {props.userTypeInfo !== "Customer" && (
                                <ReferralLink
                                  text="Promoter"
                                  backgroundColor="#9C27B0"
                                  referralUrl={`https://extraconomy.com/salepromoter?link=${props.userInfo.username}`}
                                />
                              )}
                              <ReferralLink
                                text="Partner"
                                backgroundColor="#4CAF50"
                                referralUrl={`https://extraconomy.com/businesspartner?link=${props.userInfo.username}`}
                              />
                              <ReferralLink
                                text="Seller"
                                backgroundColor="#272360"
                                referralUrl={`https://extraconomy.com/seller?link=${props.userInfo.username}`}
                              />
                              <ReferralLink
                                text="Manager"
                                backgroundColor="#F7B005"
                                referralUrl={`https://extraconomy.com/areamanager?link=${props.userInfo.username}`}
                              />
                              {props.userTypeInfo !== "Customer" && (
                                <ReferralLink
                                  text="Store"
                                  backgroundColor="#F7B"
                                  referralUrl={`https://extraconomy.com/storeregister?link=${props.userInfo.username}`}
                                />
                              )}
                            </ul>
                          </li>
                        </ul>
                      ) : (
                        <div className="text-center">
                          <i className="pi pi-lock text-7xl text-400 my-2"></i>
                          <h5 className="my-4">{localeText("planNotAllowed")}</h5>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default UserDashboardHome;
